import './index.css'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Col, Row, Spin, Typography } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import { useCookies } from 'react-cookie'
import { useNavigate } from 'react-router-dom'
import { InfoCxt } from '../../App'
import RecentPatients from './RecentPatients/RecentPatients'
import { SummaryService } from '../../services/summary-services'
import { UserService } from '../../services/user-services'
import { UserData } from '../../types/user/types'

import { ContentHeader, PaddingBox, PageBreadcrumb, PageSubtitle, PageTitle, TabHeader } from '../../components/Wrapper'
import { isHighBloodPressure } from '../../utils/isHighBloodPressure';
import { DEV_LOGIN_BYPASS } from '../../utils/devLoginBypass'

const ClickableCard: React.FC<{ className?: string; to: string; children: React.ReactNode }> = ({
  className,
  to,
  children,
}) => {
  const navigate = useNavigate()
  return (
    <div
      className={`stat-card stat-card-clickable${className ? ` ${className}` : ''}`}
      role='button'
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(to)
      }}>
      {children}
    </div>
  )
}

export default function DashBoard() {
  // get all cookies with dependency on 'loginToken'
  const [cookies] = useCookies(['loginToken'])

  const userInfo = useContext(InfoCxt)
  const isAdmin = userInfo.role === 'ROLE_ADMIN' || DEV_LOGIN_BYPASS

  /*const { loading, error, data } = useQuery(GET_PATIENT_SUMMARY, {
    context: {
      headers: {
        Authorization: cookies.loginToken ? `Bearer ${cookies.loginToken}` : '',
      },
    },
  })*/
  const userService = UserService.getInstance();
  const summaryService = SummaryService.getInstance();

  const [rankLoading, setRankLoading] = useState(true)
  const [usersLoading, setUsersLoading] = useState(true)

  const [rank1ByUser, setRank1ByUser] = useState<Record<string, string>>({})
  const [rank2ByUser, setRank2ByUser] = useState<Record<string, string>>({})
  const [frailtyByUser, setFrailtyByUser] = useState<Record<string, string>>({})

  const [allUsers, setAllUsers] = useState<UserData[]>([])

  const visibleUsers = useMemo(
    () => allUsers.filter((user) => (user as any).otherInformation?.hiddenFromPortal !== true),
    [allUsers]
  )

  const counts = useMemo(() => {
    const countMatching = (records: Record<string, string>, target: string) =>
      visibleUsers.filter((user) => records[user.userId]?.toLowerCase() === target).length
    return {
      totalPatients: visibleUsers.length,
      highBloodPressure: visibleUsers.filter(isHighBloodPressure).length,
      rank1Ati: countMatching(rank1ByUser, 'aim to improve'),
      rank2Ati: countMatching(rank2ByUser, 'aim to improve'),
      prefrail: countMatching(frailtyByUser, 'pre-frail'),
      frail: countMatching(frailtyByUser, 'frail'),
    }
  }, [visibleUsers, rank1ByUser, rank2ByUser, frailtyByUser])

  useEffect(() => {
    const fetchRankData = async () => {
      setRankLoading(true)
      const [rank1Records, rank2Records, rank2FrailtyRecords] = await Promise.all([
        summaryService.getAllRank1(),
        summaryService.getAllRank2(),
        summaryService.getAllRank2Frailty(),
      ])

      setRank1ByUser(rank1Records)
      setRank2ByUser(rank2Records)
      setFrailtyByUser(rank2FrailtyRecords)
      setRankLoading(false)
    }

    fetchRankData()
  }, [])

  useEffect(() => {
    const fetchUserData = async () => {
      setUsersLoading(true)
      const usersData = await userService.getAll()
      setAllUsers(usersData)
      setUsersLoading(false)
    }

    fetchUserData()
  }, [])

  return (
    <div className='dash-board-page'>
      <ContentHeader white={false} border>
        <PageBreadcrumb path={['Home']} />
        <TabHeader padding={false}>
          <PageTitle text='Dashboard' />
        </TabHeader>
      </ContentHeader>
      {/* Summary */}
      <PaddingBox>
        <PageSubtitle text='Overview' />
        <Spin spinning={usersLoading}>
          <div className='summary-grid'>
            <ClickableCard to='/patient'>
              <img className='stat-card-icon' src='/assets/icon/icon-patients_total.svg' alt='' />
              <div className='stat-card-body'>
                <div className='stat-card-label'>Total Patients</div>
                <div className='stat-card-num'>{counts.totalPatients}</div>
              </div>
            </ClickableCard>
            <ClickableCard to='/patient?filter=high_blood'>
              <img className='stat-card-icon' src='/assets/icon/icon-patients_high_blood.svg' alt='' />
              <div className='stat-card-body'>
                <div className='stat-card-label'>High Blood Pressures</div>
                <div className='stat-card-num'>{counts.highBloodPressure}</div>
              </div>
            </ClickableCard>
          </div>
        </Spin>

        <PageSubtitle text='Assessment' />
        <Spin spinning={rankLoading || usersLoading}>
          <div className='summary-grid'>
            <ClickableCard to='/patient?filter=rank1_ati'>
              <img className='stat-card-icon' src='/assets/icon/icon-patients_ati_rank_one.svg' alt='' />
              <div className='stat-card-body'>
                <div className='stat-card-label'>Aim to Improve - Rank One</div>
                <div className='stat-card-num'>{counts.rank1Ati}</div>
              </div>
            </ClickableCard>
            <ClickableCard to='/patient?filter=rank2_ati'>
              <img className='stat-card-icon' src='/assets/icon/icon-patients_ati_rank_two.svg' alt='' />
              <div className='stat-card-body'>
                <div className='stat-card-label'>Aim to Improve - Rank Two</div>
                <div className='stat-card-num'>{counts.rank2Ati}</div>
              </div>
            </ClickableCard>
            <div className='stat-card frailty-card'>
              <div className='stat-card-label'>Frailty</div>
              <div className='frailty-stats'>
                <ClickableCard className='frailty-stat' to='/patient?filter=prefrail'>
                  <img className='stat-card-icon' src='/assets/icon/icon-patients_prefrail.svg' alt='' />
                  <div>
                    <div className='stat-card-num'>{counts.prefrail}</div>
                    <span className='frailty-stat-label'>Pre-Frail</span>
                  </div>
                </ClickableCard>
                <ClickableCard className='frailty-stat' to='/patient?filter=frail'>
                  <img className='stat-card-icon' src='/assets/icon/icon-patients_frail.svg' alt='' />
                  <div>
                    <div className='stat-card-num'>{counts.frail}</div>
                    <span className='frailty-stat-label'>Frail</span>
                  </div>
                </ClickableCard>
              </div>
            </div>
          </div>
        </Spin>
        <br />
        {/*<PageSubtitle text='Featured Scores' />
        <List
            grid={{
              gutter: 8,
              xs: 1, // 1 column on extra-small
              sm: 2, // 2 columns on small
              md: 4, // 4 columns on medium
            }}
            dataSource={userSummary}
            renderItem={(summary) => (
              <List.Item>
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 500 }}>{summary.name}</div>
                      <p style={{ margin: 0 }}>Rank 1: {summary.rank1}</p>
                      <p style={{ margin: 0 }}>Rank 2: {summary.rank2}</p>
                      <p style={{ margin: 0 }}>Frailty: {summary.frailty}</p>
                    </div>
                    <div style={{ width: '48px', height: '48px' }}>
                      <img
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        src={
                          summary.icon && summary.icon.toLowerCase() !== ''
                            ? `/assets/icon/icon-${summary.icon.toLowerCase()}.svg`
                            : '/assets/icon/icon-user.svg'
                        }
                        alt="icon"
                      />
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />*/}

        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <PageSubtitle text='Recent Patients' tooltip='Showing the 50 most recently active patients.' />
            <RecentPatients
              users={allUsers}
              rank1ByUser={rank1ByUser}
              rank2ByUser={rank2ByUser}
              frailtyByUser={frailtyByUser}
              loading={usersLoading || rankLoading}
              isAdmin={isAdmin}
            />
          </Col>
        </Row>

        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            {/* Appointments */}
            <PageSubtitle text='Appointments' />
            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: '48px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
              }}>
              <CalendarOutlined style={{ fontSize: 32, color: '#BFBFBF' }} />
              <Typography.Text strong>Appointments are coming soon</Typography.Text>
              <Typography.Text type='secondary' style={{ textAlign: 'center' }}>
                You&apos;ll be able to view and manage your patient appointments here.
              </Typography.Text>
            </div>
          </Col>
        </Row>
      </PaddingBox>
    </div>
  )
}
